import { ICase } from '@brands/services/cases/types/ICase';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';

import { ActiveVideoRoomParticipant } from '../types/consultationPage.types';

const DISCONNECTED_VIDEO_PARTICIPANTS_STORAGE_KEY = 'consultationPageDisconnectedVideoParticipants';
const DISCONNECTED_VIDEO_PARTICIPANT_TTL_MS = 2 * 60 * 60 * 1000;

export const VIDEO_PARTICIPANT_PRESENCE_EVENT = 'consultation-video-participant-presence';

export type VideoParticipantPresenceCommand = 'participant_connected' | 'participant_disconnected';

export type VideoParticipantPresenceEventDetail = {
  caseId: number;
  command: VideoParticipantPresenceCommand;
  participantIdentities: Array<number | string>;
};

type DisconnectedVideoParticipantsByCase = Record<string, Record<string, number>>;

const normalizeVideoParticipantKey = (value: unknown): string | null => {
  if (value === undefined || value === null || value === '') return null;
  return String(value);
};

const uniqueParticipantKeys = (values: unknown[]): string[] =>
  Array.from(new Set(values.map(normalizeVideoParticipantKey).filter((value): value is string => Boolean(value))));

const normalizeRoleName = (value: unknown): UserRoleName | null => {
  if (value === undefined || value === null || value === '') return null;

  const normalizedRoleName = String(value).replace(/[\s_-]/g, '').toLowerCase();

  switch (normalizedRoleName) {
    case 'patient':
      return UserRoleName.Patient;
    case 'dependent':
      return UserRoleName.Dependent;
    case 'provider':
      return UserRoleName.Provider;
    case 'careadvisor':
      return UserRoleName.CareAdvisor;
    case 'collector':
      return UserRoleName.Collector;
    case 'clientadmin':
    case 'organizationadmin':
      return UserRoleName.OrganizationAdmin;
    default:
      return null;
  }
};

const getCaseParticipantKeysByRole = (roleName: UserRoleName | null, currentCase?: ICase): unknown[] => {
  if (!roleName || !currentCase) return [];

  switch (roleName) {
    case UserRoleName.Patient:
    case UserRoleName.Dependent:
      return [currentCase.patient?.guardian_id, currentCase.patient_id, currentCase.patient?.id];
    case UserRoleName.Provider:
      return [currentCase.provider_id, currentCase.provider?.id];
    case UserRoleName.CareAdvisor:
      return [currentCase.care_advisor_id, currentCase.care_advisor?.id];
    case UserRoleName.Collector:
      return [currentCase.collector_id, currentCase.collector?.id];
    default:
      return [];
  }
};

const readDisconnectedVideoParticipants = (): DisconnectedVideoParticipantsByCase => {
  if (typeof window === 'undefined') return {};

  try {
    const stored = window.localStorage.getItem(DISCONNECTED_VIDEO_PARTICIPANTS_STORAGE_KEY);
    if (!stored) return {};

    const parsed = JSON.parse(stored);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeDisconnectedVideoParticipants = (participants: DisconnectedVideoParticipantsByCase): void => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(DISCONNECTED_VIDEO_PARTICIPANTS_STORAGE_KEY, JSON.stringify(participants));
  } catch {
    // Best effort only; live room events and polling still handle the normal path.
  }
};

export const getVideoParticipantKeys = (
  participant: ActiveVideoRoomParticipant | null | undefined,
  currentCase?: ICase,
): string[] => {
  if (!participant) return [];

  const roleName = normalizeRoleName(participant.user?.role?.name);

  return uniqueParticipantKeys([
    participant.identity,
    participant.user?.id,
    ...getCaseParticipantKeysByRole(roleName, currentCase),
  ]);
};

export const getVideoParticipantKeysFromPresenceMessage = (
  message: Record<string, any>,
  currentCase: ICase,
): string[] => {
  const directKeys = uniqueParticipantKeys([
    message.participant_identity,
    message.participantIdentity,
    message.identity,
    message.user_id,
    message.userId,
    message.user?.id,
    message.participant_id,
    message.participantId,
    message.participant?.identity,
    message.participant?.user_id,
    message.participant?.userId,
    message.participant?.user?.id,
    message.participant_user_id,
    message.participantUserId,
  ]);

  const roleName = normalizeRoleName(
    message.participant_role ||
      message.participantRole ||
      message.role?.name ||
      message.role ||
      message.user?.role?.name ||
      message.participant?.role?.name ||
      message.participant?.role ||
      message.participant?.user?.role?.name,
  );
  const roleKeys = getCaseParticipantKeysByRole(roleName, currentCase);

  return uniqueParticipantKeys([...directKeys, ...roleKeys]);
};

export const rememberDisconnectedVideoParticipants = (
  caseId: number,
  participantIdentities: Array<number | string>,
): void => {
  const participantKeys = uniqueParticipantKeys(participantIdentities);
  if (!participantKeys.length) return;

  const disconnectedParticipants = readDisconnectedVideoParticipants();
  const caseKey = String(caseId);
  const disconnectedAt = Date.now();

  disconnectedParticipants[caseKey] = {
    ...(disconnectedParticipants[caseKey] || {}),
    ...participantKeys.reduce<Record<string, number>>((acc, participantKey) => {
      acc[participantKey] = disconnectedAt;
      return acc;
    }, {}),
  };

  writeDisconnectedVideoParticipants(disconnectedParticipants);
};

export const forgetDisconnectedVideoParticipants = (
  caseId: number,
  participantIdentities: Array<number | string>,
): void => {
  const participantKeys = uniqueParticipantKeys(participantIdentities);
  if (!participantKeys.length) return;

  const disconnectedParticipants = readDisconnectedVideoParticipants();
  const caseKey = String(caseId);

  if (!disconnectedParticipants[caseKey]) return;

  participantKeys.forEach((participantKey) => {
    delete disconnectedParticipants[caseKey][participantKey];
  });

  if (Object.keys(disconnectedParticipants[caseKey]).length === 0) {
    delete disconnectedParticipants[caseKey];
  }

  writeDisconnectedVideoParticipants(disconnectedParticipants);
};

export const isDisconnectedVideoParticipant = (
  caseId: number,
  participant: ActiveVideoRoomParticipant,
  currentCase?: ICase,
): boolean => {
  const disconnectedParticipants = readDisconnectedVideoParticipants();
  const caseKey = String(caseId);
  const participantKeys = getVideoParticipantKeys(participant, currentCase);
  const participantStartedAt = participant.start_time ? Date.parse(participant.start_time) : Number.NaN;
  const activeDisconnectedKeys = participantKeys.filter((participantKey) => {
    const disconnectedAt = disconnectedParticipants[caseKey]?.[participantKey];

    if (!disconnectedAt) return false;

    if (Date.now() - disconnectedAt > DISCONNECTED_VIDEO_PARTICIPANT_TTL_MS) {
      delete disconnectedParticipants[caseKey][participantKey];
      return false;
    }

    if (Number.isFinite(participantStartedAt) && participantStartedAt > disconnectedAt) {
      delete disconnectedParticipants[caseKey][participantKey];
      return false;
    }

    return true;
  });

  if (disconnectedParticipants[caseKey] && Object.keys(disconnectedParticipants[caseKey]).length === 0) {
    delete disconnectedParticipants[caseKey];
  }

  writeDisconnectedVideoParticipants(disconnectedParticipants);

  return activeDisconnectedKeys.length > 0;
};

export const dispatchVideoParticipantPresenceEvent = (detail: VideoParticipantPresenceEventDetail): void => {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(
    new CustomEvent<VideoParticipantPresenceEventDetail>(VIDEO_PARTICIPANT_PRESENCE_EVENT, { detail }),
  );
};
